import { z } from "zod";
import type { ThemeColor } from "./theme.js";

// ---------------------------------------------------------------------------
// 製品（入口）とモジュール（機能の門）（営業力 E0-a、049、../eigyouryoku/docs/02 §3〜§5。射場さん 2026-09-26 の決定）
//   - 現場力（genbaryoku.jp）と営業力（eigyouryoku.jp）は、同じコード・同じ DB・同じサーバーで動く別の製品。ドメインだけ別
//   - 会社（tenants.product）はどちらか1つの製品に属する。会員登録のドメインで決まり、あとから変えない
//   - 製品で変わるのは 見た目（名前・ロゴ・印の色）・言葉の既定・料金表・法務3点・メールの差出人。DB・API のパス・帳票のエンジンは変わらない
//   - 機能の門は「モジュール」（tenant_modules）。API の requireModule() 1か所（違えば 404 MODULE_OFF）
//   - 製品の判定は3か所だけ：ログイン前は Host ヘッダ（API）／location.hostname（画面）、ログイン後は Me.tenant.product、紙は出した会社の product
// ---------------------------------------------------------------------------

export const Product = z.enum(["genba", "eigyo"]);
export type Product = z.infer<typeof Product>;
export const PRODUCT_ORDER: readonly Product[] = ["genba", "eigyo"];
/** 知らないホスト・省いたときの製品（今の会社は全部これ） */
export const DEFAULT_PRODUCT: Product = "genba";

/**
 * モジュール（tenant_modules.module）。
 *   core＝どの製品でも／field＝現場力の既定（現地調査・写真・外注見積の突合）／sales＝両方の製品の既定（納品・締め請求・入金・発注・仕入。現場力は 2026-09-29 から）／
 *   stock＝在庫（オプション）／paper_design＝自分の書式（オプション）／schedule＝工程管理（オプション、#100。現場力だけ）／
 *   doc_read＝書類の読み取り（オプション、受け取った書類の2回目。両製品・既定 OFF＝製品の既定にもお試しにも入れない。運営が管理アプリで ON）／
 *   referral＝パートナー手数料（#213、docs/40。両製品・既定 OFF。会社が 設定 → 見積・請求 で ON/OFF）
 */
export const Module = z.enum(["core", "field", "sales", "stock", "paper_design", "schedule", "doc_read", "referral"]);
export type Module = z.infer<typeof Module>;

export type ProductSpec = {
  /** 画面・メールの【 】・法務3点に出す名前 */
  name: string;
  /** 読み（メールの末尾「現場力（げんばりょく）」。E0-b） */
  reading: string;
  shortName: string;
  /** 本番のホスト（www. も同じ製品。PRODUCT_HOSTS） */
  host: string;
  /** 言葉の既定（013 の vocabulary_preset。DB の fn_vocabulary_default と同じ答え） */
  vocabulary: "construction" | "general";
  /** 会員登録で入るモジュール（DB の fn_auth_signup と同じ） */
  defaultModules: readonly Module[];
  /**
   * お試しのオプション（20261006_1900、2026-10-06 射場さん）：受付開始の前に作る会社だけ、既定のモジュールと一緒に入る（現場力＝schedule）。
   * 受付開始のあとに作る会社には入らない（#69 でオプションを買ったときに入る。営業力も同じ）。DB の fn_auth_signup ほかの p_trial_options と同じ。判定は API の trialOptionsOpen
   */
  trialModules: readonly Module[];
  brand: {
    /** 角印の色。現場力は緑（docs/10 §4.3）、営業力は青 B2（2026-10-01 決定、docs/10 §4.3.1） */
    seal: string;
    /** テーマ色の既定（#191、docs/77）。人の設定（accounts.theme_color）が無いとき・ログイン前の画面。現場力＝藍、営業力＝濃い青 */
    theme: ThemeColor;
  };
  /**
   * 分割の予定の既定の回（#142 段1、docs/52 §6）。名前・割合（null＝残り）・今日から何か月後。
   *   現場力＝着手金・中間金・完成時、営業力＝前金・中間金・残金（契約の決まりが違う）。会社の設定で変えるのは段3
   */
  installments: readonly { label: string; percent: number | null; months: number; /** 新しい案件の「分割」の絵の下の短い名前 */ short: string }[];
  /**
   * 製品ごとの言い回し（#142 段5・段6、docs/54）。決まり文には {pw:formsFrom} のように書き、fillProduct（画面の tp()・API）が埋める。
   * 会社が言い換えられる言葉（{customer} など 17）は vocabulary.ts。ここは会社で変えない、製品で決まる言い回しだけ
   */
  words: {
    /** 専用帳票・様式を渡してくる相手（No.30・40・41・51）：元請／取引先 */
    formsFrom: string;
    /** 率の行の例（No.22）：法定福利費・諸経費など／手数料・諸経費など */
    rateExample: string;
    /** 区分の2段の例（No.23）：仮設工事 ＞ 足場／デザイン ＞ ロゴ */
    tradeExample: string;
    /** 支店の宛名の例（No.50）：○○建設 大阪支店／○○商事 大阪支店 */
    branchExample: string;
    /** 案件の例（No.16）：射場邸改装工事 など／〇〇社 Webサイト制作 など */
    projectExample: string;
    /** 工程表を始める日（段5 着工・雨）：着工日／開始日 */
    startDay: string;
    /** 同じく短い形：着工／開始 */
    start: string;
    /** 工程表のオプションの名前（No.90）：工程管理／スケジュール管理 */
    scheduleOption: string;
    /** 工程表の中止のボタン・報告の種類（No.125）：雨で中止／中止（営業力は雨を前に出さない） */
    stopRain: string;
    /** 中止の窓の「{task}を選ぶ」の下（No.128）：雨で止まる工程だけ／一部だけ止める */
    stopRowsSub: string;
    /** 中止の窓の「{siteShort}全体」の下（No.128）：台風など／先方の都合など */
    stopAllSub: string;
    /** {project}の一覧の「進行中」の中の、継続案件でない{project}の区切り（#289、Figma ④）：工事／案件 */
    regularGroups: string;
    /** ヘルプの記事「送付状」の会社の初めの値（#303 B1。cover と同じ答え） */
    coverDefault: string;
  };
  /** 帳票エディタの文字の部品の定型文（No.37・38）。製品で違うのは お支払い条件 と 変更の断り書き */
  paperPhrases: readonly string[];
  /** 見積依頼の既定（#119、docs/56 §7.4）。設定「見積依頼」で会社が変えた分は tenants.rfq_settings。差し込みは ［宛名］［番号］… */
  /** 見積依頼の既定（#218：回答期限は空欄＝null。設定で日数を決めた会社だけその日数） */
  rfq: { replyDays: number | null; terms: string; mailSubject: string; mailBody: string };
  /**
   * 送付状の既定（#303 B1、2026-11-04 経理担当の指摘 12）：帳票の送信の画面で送付状を付けた形で始めるか。会社が 設定 → 送付状 で変えた分は tenants.cover_letter_settings。
   * 現場力＝印刷はオン・メールはオフ（前のまま）、営業力＝どちらもオフ（請求書をメールで送るのが主で、送付状の要らない会社が多い）
   */
  cover: { print: boolean; mail: boolean };
  /** 画面の資産（apps/web/public の下）。営業力は apps/web/public/eigyo/（2026-10-01 の決定版。文字はアウトライン化済み） */
  assets: {
    logoHeader: string;
    /** 暗い地用（題字が白。#191 の2回目のダーク。screen_sample/logo の final と final_eigyo の header_dark.svg） */
    logoHeaderDark: string;
    logoMark: string;
    logo: string;
    favicon: string;
    manifest: string;
    appleTouchIcon: string;
  };
  /** お問い合わせ（Legal.tsx の Email と同じ組み立て式。ソースにメールアドレスをそのまま書かない） */
  support: { user: string; domain: string };
  /** ログイン前の紹介の一言（apps/web の LoginAside） */
  copy: {
    /** だれ向けか（kicker） */
    audience: string;
    /** 一行（2行に割る） */
    lead: readonly [string, string];
    /** 法務3点の「本サービス」の説明（利用規約 第2条） */
    service: string;
    /** ログインの「ログインしたままにする」の「？」（#142 段1 D17） */
    keepLogin: string;
  };
};

export const PRODUCTS: Record<Product, ProductSpec> = {
  genba: {
    name: "現場力",
    reading: "げんばりょく",
    shortName: "現場力",
    host: "genbaryoku.jp",
    vocabulary: "construction",
    defaultModules: ["core", "field", "sales"],
    trialModules: ["schedule"],
    installments: [
      { label: "着手金", percent: 30, months: 0, short: "着手" },
      { label: "中間金", percent: 30, months: 1, short: "中間" },
      { label: "完成時", percent: null, months: 3, short: "完成" },
    ],
    brand: { seal: "#00804C", theme: "indigo" },
    words: {
      formsFrom: "元請",
      rateExample: "法定福利費・諸経費など",
      tradeExample: "仮設工事 ＞ 足場",
      branchExample: "○○建設 大阪支店",
      projectExample: "射場邸改装工事 など",
      startDay: "着工日",
      start: "着工",
      scheduleOption: "工程管理",
      stopRain: "雨で中止",
      stopRowsSub: "雨天で止まる工程のみ",
      stopAllSub: "台風など",
      regularGroups: "工事",
      coverDefault: "初めは「印刷に添付」がオン、「メールに添付」がオフです。",
    },
    rfq: {
      replyDays: null,
      // 2025-12 全面施行の改正建設業法（材料費・労務費等の内訳の明示）に合わせる
      terms: "材料費・労務費・法定福利費を分けてお見積りください。現地の確認が必要なときはご連絡ください。",
      mailSubject: "見積依頼 ［番号］ ［件名］",
      mailBody: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n下記のとおりお見積りをお願いいたします。詳しくは添付の見積依頼書をご覧ください。\n\n番号：［番号］\n件名：［件名］\n回答期限：［回答期限］\n\nよろしくお願いいたします。",
    },
    cover: { print: true, mail: false },
    paperPhrases: ["御中", "様", "下記のとおり御見積り申し上げます。", "下記のとおり御請求申し上げます。", "上記金額には消費税を含みます", "お振込手数料はご負担ください", "有効期限：発行日より30日", "お支払い条件：工事完了後 翌月末 お振込", "この見積書は現場調査後に変更となる場合があります", "ご不明な点は担当までご連絡ください", "印", "担当者印", "承認印"],
    assets: { logoHeader: "/logo-header.svg", logoHeaderDark: "/logo-header-dark.svg", logoMark: "/logo-mark.svg", logo: "/logo.svg", favicon: "/favicon.ico", manifest: "/manifest.webmanifest", appleTouchIcon: "/icon-180.png" },
    support: { user: "support", domain: "genbaryoku.jp" },
    copy: {
      audience: "工務店・塗装・大工・電気工事のための 見積〜請求",
      lead: ["現場で見積の土台をつくり、", "社内で仕上げる。"],
      service: "見積書・発注書・発注請書・納品書・検収書・請求書・領収書・見積依頼書・支払通知書その他の帳票の作成及びこれに付随する業務の効率化を目的とするクラウド型の業務システム",
      keepLogin: "現場のスマホや自分のパソコンなら、毎回入れなくて済みます。共用のパソコンでは外してください。",
    },
  },
  eigyo: {
    name: "営業力",
    reading: "えいぎょうりょく",
    shortName: "営業力",
    host: "eigyouryoku.jp",
    vocabulary: "general",
    defaultModules: ["core", "sales"],
    trialModules: [],
    installments: [
      { label: "前金", percent: 30, months: 0, short: "前金" },
      { label: "中間金", percent: 30, months: 1, short: "中間" },
      { label: "残金", percent: null, months: 3, short: "残金" },
    ],
    brand: { seal: "#1F5FA8", theme: "blue" },
    words: {
      formsFrom: "取引先",
      rateExample: "手数料・諸経費など",
      tradeExample: "デザイン ＞ ロゴ",
      branchExample: "○○商事 大阪支店",
      projectExample: "〇〇社 Webサイト制作 など",
      startDay: "開始日",
      start: "開始",
      scheduleOption: "スケジュール管理",
      stopRain: "中止",
      stopRowsSub: "一部のみ中止",
      stopAllSub: "先方の都合など",
      regularGroups: "案件",
      coverDefault: "初めは「印刷に添付」「メールに添付」のどちらもオフです。",
    },
    rfq: {
      replyDays: null,
      terms: "納期・送料・お支払い条件を明記してお見積りください。",
      mailSubject: "見積依頼 ［番号］ ［件名］",
      mailBody: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n下記のとおりお見積りをお願いいたします。詳しくは添付の見積依頼書をご覧ください。\n\n番号：［番号］\n件名：［件名］\n回答期限：［回答期限］\n\nよろしくお願いいたします。",
    },
    cover: { print: false, mail: false },
    paperPhrases: ["御中", "様", "下記のとおり御見積り申し上げます。", "下記のとおり御請求申し上げます。", "上記金額には消費税を含みます", "お振込手数料はご負担ください", "有効期限：発行日より30日", "お支払い条件：納品後 翌月末 お振込", "この見積書は仕様の確定後に変更となる場合があります", "ご不明な点は担当までご連絡ください", "印", "担当者印", "承認印"],
    assets: {
      logoHeader: "/eigyo/logo-header.svg",
      logoHeaderDark: "/eigyo/logo-header-dark.svg",
      logoMark: "/eigyo/logo-mark.svg",
      logo: "/eigyo/logo.svg",
      favicon: "/eigyo/favicon.ico",
      manifest: "/eigyo/manifest.webmanifest",
      appleTouchIcon: "/eigyo/icon-180.png",
    },
    support: { user: "support", domain: "eigyouryoku.jp" },
    copy: {
      audience: "スタートアップ・個人事業主・中小企業のための 案件〜入金",
      lead: ["見積を出したら、受注・納品・請求・入金まで", "同じ画面で追いかける。"],
      service: "案件・見積書・受注・納品書・請求書・領収書・入金・発注書・仕入・在庫その他の販売に関する帳票の作成及びこれに付随する業務の効率化を目的とするクラウド型の業務システム",
      keepLogin: "自分のスマホやパソコンなら、毎回入れなくて済みます。共用のパソコンでは外してください。",
    },
  },
};

/** ホスト名 → 製品（本番）。.env の PRODUCT_HOSTS の既定と同じ。www. も同じ製品 */
export const PRODUCT_HOSTS: Record<string, Product> = {
  "genbaryoku.jp": "genba",
  "www.genbaryoku.jp": "genba",
  "eigyouryoku.jp": "eigyo",
  "www.eigyouryoku.jp": "eigyo",
};
/** .env の PRODUCT_HOSTS の形（apps/api の config.ts が読む） */
export const PRODUCT_HOSTS_DEFAULT = Object.entries(PRODUCT_HOSTS)
  .map(([h, p]) => `${h}=${p}`)
  .join(",");

/** "genbaryoku.jp=genba,eigyouryoku.jp=eigyo" → 表。知らない製品名・形の崩れは捨てる */
export function parseProductHosts(s: string | undefined): Record<string, Product> {
  const out: Record<string, Product> = {};
  for (const pair of (s ?? "").split(",")) {
    const [h, p] = pair.split("=").map((x) => x.trim().toLowerCase());
    const parsed = Product.safeParse(p);
    if (h && parsed.success) out[h] = parsed.data;
  }
  return out;
}

/** ホスト名（ポートは外す）→ 製品。知らないホストは null（呼ぶ側が DEFAULT_PRODUCT や x-product に落とす） */
export function productOfHost(hostname: string | undefined, hosts: Record<string, Product> = PRODUCT_HOSTS): Product | null {
  if (!hostname) return null;
  const h = hostname.trim().toLowerCase().replace(/:\d+$/, "");
  return hosts[h] ?? null;
}

/** 開発・テストで製品を指すヘッダー（本番の既知のホストでは無視する）。画面は VITE_PRODUCT か ?product= のとき付ける */
export const PRODUCT_HEADER = "x-product";

/** 決まり文の {product}（製品名）・{productHost}（ドメイン）を置き換える。{customer} などの言葉は fillLabels */
export function fillProduct(text: string, product: Product): string {
  const p = PRODUCTS[product];
  return text
    .replace(/\{productHost\}/g, p.host)
    .replace(/\{product\}/g, p.name)
    .replace(/\{pw:(\w+)\}/g, (m, k: string) => (k in p.words ? p.words[k as keyof ProductSpec["words"]] : m));
}

export const PRODUCT_MSG = {
  /** requireModule の 404（RFC 9457、code MODULE_OFF） */
  moduleOff: "この機能はお使いのプランに含まれていません",
  /** 会社を選ぶ画面：違う製品の会社（薄く出す） */
  otherProduct: (p: Product) => `${PRODUCTS[p].name}の会社です。${PRODUCTS[p].host} でお使いください`,
  /** POST /auth/choose・/auth/switch の 403（code PRODUCT_MISMATCH） */
  productMismatch: (p: Product) => `この会社は${PRODUCTS[p].name}の会社です。${PRODUCTS[p].host} でお使いください`,
} as const;
